import { useCallback, useSyncExternalStore } from 'react';
import {
  getAudioLoadProgressSnapshot,
  subscribeAudioLoadProgress,
} from '../../game/audio/bufferCache';

/**
 * Covers the canvas during the brief cold-start gap between tapping Start
 * and the first rendered frame, while the tapped-for AudioContext fetches
 * and decodes the track. On most devices that gap is a few milliseconds and
 * this never becomes visible — the CSS fade-in is delayed past the typical
 * decode time (see `.play-loading-overlay`), so only genuinely slow starts
 * (older iPhones decoding a multi-MB MP3) reveal it. Without it the player
 * stares at a black canvas with no feedback after the start screen vanishes.
 *
 * When it does show, it reports the track's live download progress
 * (received / total KB plus a bar) by subscribing to the byte-stream the
 * `bufferCache` reports — the same download the chart-load preload kicked
 * off, so on a slow connection the bar reflects whatever is still in flight.
 *
 * Call sites gate this on the first loop (`getLoopIndex() === 0`): inter-loop
 * restarts re-enter the `'starting'` status too, but their audio buffer is
 * already cached/decoded, so the overlay would be a meaningless flash there
 * (the speed-up banner / count-in already cover that transition).
 */
export function PlayLoadingOverlay({ audioSrc }: { audioSrc: string }) {
  const subscribe = useCallback(
    (onChange: () => void) => subscribeAudioLoadProgress(audioSrc, onChange),
    [audioSrc],
  );
  const getSnapshot = useCallback(
    () => getAudioLoadProgressSnapshot(audioSrc),
    [audioSrc],
  );
  const progress = useSyncExternalStore(subscribe, getSnapshot);

  const received = progress?.receivedBytes ?? 0;
  const total = progress?.totalBytes ?? null;
  const determinate = total !== null && total > 0;
  // Clamp the shown total up to received: a gzip/transfer-encoding proxy can
  // inflate the streamed bytes past the advertised Content-Length, and a
  // "5,000 / 3,000 KB" label (or a >100% bar) would just look broken.
  const displayTotal = determinate ? Math.max(total, received) : null;
  const pct = displayTotal ? (received / displayTotal) * 100 : 0;
  const label = displayTotal
    ? `${formatKb(received)} / ${formatKb(displayTotal)} KB`
    : received > 0
      ? `${formatKb(received)} KB`
      : '';

  return (
    <div
      className="play-overlay play-loading-overlay"
      data-testid="play-loading-overlay"
      role="status"
      aria-live="polite"
    >
      <div className="play-loading-inner">
        <p className="play-loading-text">Loading track…</p>
        <div
          className={`play-loading-progress${determinate ? '' : ' is-indeterminate'}`}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={determinate ? 100 : undefined}
          aria-valuenow={determinate ? Math.round(pct) : undefined}
        >
          <div
            className="play-loading-progress-fill"
            style={determinate ? { width: `${pct}%` } : undefined}
          />
        </div>
        {label && <p className="play-loading-bytes">{label}</p>}
      </div>
    </div>
  );
}

function formatKb(bytes: number): string {
  return Math.round(bytes / 1024).toLocaleString();
}
